/**
 * Первая страница: что это и почему это не таблица с процентами.
 *
 * Проверка, которой страница обязана соответствовать: закрой шапку — и по
 * тексту всё ещё должно быть понятно, что это не «ещё один сканер вилок».
 * Отсюда правило содержания: каждый блок называет решение, принятое против
 * привычного способа, а не свойство продукта. «Показываем ROI» — свойство,
 * которое есть у всех. «Показываем рубли, которые дойдут до вашего банка после
 * стакана и лимитов» — решение, которое стоило нам протокола.
 *
 * Цифр из ленты здесь нет намеренно. Скриншот с «ROI 7.4%» на первой странице
 * — обещание, которое продукт обязан выполнять каждый день, а рынок такого не
 * обещает никому.
 *
 * С 12.08.2026 это ещё и ДВЕРЬ, а не только витрина: на контуре со входом лента
 * закрыта целиком, и первый экран обязан кончаться одним действием (владелец:
 * «никакой ленты, страница входа должна быть»). Отсюда раскладка: заголовок,
 * объяснение на три предложения и кнопка — всё это помещается в один экран без
 * прокрутки. Всё остальное живёт ниже сгиба и читается по желанию.
 */

import { useEffect, useState } from 'react';

import { Login } from '../access/Login';
import { useSession } from '../access/session';
import { Who } from '../access/Who';
import { APP_LABEL, apiUrl } from '../lib/build';
import { appHref } from './route';
import { PhoneLanding } from './phone/PhoneLanding';
import { usePhone } from './phone/usePhone';

/** Контур с чужой лентой. Метка ставится сборкой (`VITE_APP_LABEL`). */
const MIRROR_CONTOUR = APP_LABEL === 'V2';

const POINTS: { head: string; text: string }[] = [
  {
    head: 'прибыль сразу в рублях',
    text:
      'Пять процентов выглядят красиво ровно до той минуты, когда выясняется: больше ' +
      'тысячи рублей туда не влезет, и вся прибыль - шестьдесят рублей. Мы заранее ' +
      'считаем, сколько денег контора и биржа примут на самом деле, и пишем сумму, ' +
      'которая выйдет при вашем банке.',
  },
  {
    head: 'успеете или нет, видно заранее',
    text:
      'Сканер запоминает, за сколько секунд вы обычно ставите обе ноги, и сравнивает ' +
      'это с тем, сколько живут такие вилки. Если не успеваете, строка помечена ещё до ' +
      'того, как вы открыли первый купон.',
  },
  {
    head: 'вилка пропала, и мы скажем почему',
    text:
      'Линию закрыли, деньги на бирже кончились, кэфы разъехались, прибыль съела ' +
      'комиссия. Причину мы пишем рядом. Строка, которая исчезает молча, отучает ' +
      'верить ленте.',
  },
  {
    head: 'одна вилка на экране',
    text:
      'Витрина показывает ту, которую стоит брать прямо сейчас, с готовыми суммами по ' +
      'обеим ногам. Лента на сто строк тоже есть, но она для тех, кто уже освоился.',
  },
];

/**
 * Чем эти два обещания отличаются на контурах.
 *
 * Оба до 06.08.2026 стояли общими и оба на зеркальном контуре были неправдой:
 * бота там нет вовсе (читать обновления одного бота вправе один процесс, и он
 * занят боевым), а вход через Telegram выключен. Человек читал про сигнал,
 * заходил и не находил ни бота, ни аккаунта — то есть первое, что делал
 * продукт, это обманывал.
 *
 * Правкой стало не вычёркивание, а замена на то, что там правда есть: звук со
 * значком в браузере вместо бота и пароль вместо аккаунта. Вычеркнуть было бы
 * проще, но пустое место на витрине читается как «этого нет», а оно есть.
 */
const OURS: { head: string; text: string }[] = [
  {
    head: 'сигнал в Telegram',
    text:
      'Бот напишет, когда появится вилка дороже вашего порога. Порог вы ставите ' +
      'в рублях прибыли. В тихие часы, которые вы сами назначите, бот молчит.',
  },
  {
    head: 'аккаунт на одного',
    text:
      'Одна живая лента на один вход. Откроете её на втором экране - первый ' +
      'отключится, а вам придёт сообщение. Банк, лимиты и скорость у каждого свои, ' +
      'поэтому в общем аккаунте эти числа мешают обоим.',
  },
];

const MIRROR: { head: string; text: string }[] = [
  {
    head: 'сигнал звуком и в углу экрана',
    text:
      'Вилка дороже вашего порога подаёт голос и всплывает значком браузера, даже если ' +
      'вкладка свёрнута. Порог вы ставите в рублях прибыли.',
  },
  {
    head: 'пароль на одного',
    text:
      'Доступ выдаётся личным паролем, и живая лента одна на пароль. Второй экран ' +
      'заберёт её у первого и объяснит, почему. Банк, лимиты и замеренная скорость ' +
      'привязаны к паролю, поэтому переживут и новую машину, и очистку кэша.',
  },
];

/**
 * Обещания витрины для этого контура.
 *
 * Отдельной функцией, чтобы проверяемое утверждение было проверяемым: метка
 * контура приходит из сборки и в тесте не переключается, а вопрос «не обещает
 * ли зеркало того, чего у него нет» задать надо.
 */
export function promises(mirror: boolean): { head: string; text: string }[] {
  return [...POINTS, ...(mirror ? MIRROR : OURS)];
}

/**
 * Чего не хватает вошедшему человеку. Причина главнее общей фразы.
 *
 * «Доступ закрыт» одинаково звучит для того, кого не звали, и для того, у кого
 * вчера кончился месяц. Первому идти к тому, кто его позвал, второму — просить
 * продления, и отправить обоих в одно место значит удвоить нам переписку.
 */
export function lack(reason: string | null): string {
  if (reason === 'expired') {
    return (
      'Срок доступа вышел. Продление занимает секунду: попросите его у того, кто вас ' +
      'позвал, и лента оживёт сама - выходить и входить заново не нужно.'
    );
  }
  if (reason === 'revoked') {
    return 'Доступ закрыт. Если это недоразумение - напишите тому, кто вас позвал.';
  }
  return (
    'Вход есть, доступа пока нет: ленту открывает личное приглашение. Если вас звали - ' +
    'откройте ссылку, которую вам дали, прямо в Telegram: она откроет доступ сама.'
  );
}

/**
 * До какого числа открыт доступ, словами человека.
 *
 * Строка, а не дата, приходит с сервера, и разобрать её может не получиться:
 * старый сервер такого поля не знал вовсе. Тогда строка не рисуется — пустое
 * место лучше, чем «доступ до Invalid Date».
 */
export function until(iso: string | null): string | null {
  if (iso === null) return null;
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

/** «1 вилка», «44 вилки», «5 вилок». Счётчик с чужим окончанием читается как ошибка. */
export function forks(count: number): string {
  const ten = count % 10;
  const hundred = count % 100;
  if (ten === 1 && hundred !== 11) return `${count} вилка`;
  if (ten >= 2 && ten <= 4 && (hundred < 12 || hundred > 14)) return `${count} вилки`;
  return `${count} вилок`;
}

/**
 * Сколько вилок в кадре прямо сейчас. Единственное живое число на этой странице.
 *
 * Ленты здесь нет и не будет: неприглашённому сервер отдаёт пустой список
 * (`forq.access.demo.shut`), и в ответе остаётся ровно один счётчик. Он ничего
 * не выдаёт — ни матча, ни площадки, ни отдачи, — но отвечает на вопрос, на
 * который никакой текст не отвечает: работает ли это сейчас или страница висит
 * с прошлого года.
 *
 * Спрашиваем ОДИН раз за открытие страницы. Опрос по таймеру был бы честнее на
 * секунду и дороже навсегда: десятки обращений к `/v1/signals` без сокета
 * сторож трафика считает признаком съёмщика — и правильно делает.
 *
 * Ноль и отказ выглядят одинаково: строка просто не появляется. «В кадре 0
 * вилок» на двери — это не честность, а приглашение уйти, и в те минуты, когда
 * рынок действительно пуст, продукт о себе рассказывает молчанием.
 */
function Alive(): React.ReactElement | null {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const answer = await fetch(apiUrl('/v1/signals?limit=1'), {
          credentials: 'same-origin',
        });
        const body = (await answer.json()) as { activeForks?: unknown };
        const seen = body.activeForks;
        if (live && typeof seen === 'number' && seen > 0) setCount(seen);
      } catch {
        // Молчим намеренно: счётчик — украшение двери, а не её работа.
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  if (count === null) return null;
  return (
    <p className="nx-land__alive">
      <i className="nx-land__pulse" aria-hidden="true" />
      сканер работает: в кадре {forks(count)}
    </p>
  );
}

/**
 * Как сюда попадают. Три шага, потому что их ровно три.
 *
 * Раздел появился вместе с приглашениями: до них вход был единственным шагом и
 * объяснять было нечего. Теперь человек, нашедший бота поиском, упирается в
 * отказ, и страница обязана сказать, чего ему не хватает, ДО того как он
 * потратит на это попытку.
 */
function Steps({ bot }: { bot: string | null }): React.ReactElement {
  const steps = [
    {
      head: 'приглашение',
      text:
        'Доступ открывает личная ссылка от владельца. Она именная и одноразовая: ' +
        'пересланная дальше по чату не сработает.',
    },
    {
      head: 'вход',
      text:
        `Кнопка выше выдаёт код и ведёт в бота${bot === null ? '' : ` @${bot}`}. Пароля нет: ` +
        'бот узнаёт вас сам, он же присылает сигналы.',
    },
    {
      head: 'лента',
      text:
        'Сканер откроется в этой же вкладке. Экран один на человека: второй ' +
        'спросит подтверждения в Telegram.',
    },
  ];
  return (
    <section className="nx-land__how">
      <h2 className="nx-land__how-head">как получить доступ</h2>
      <ol className="nx-land__steps">
        {steps.map((step, index) => (
          <li key={step.head} className="nx-land__step">
            <b className="nx-land__num">{String(index + 1).padStart(2, '0')}</b>
            <h3>{step.head}</h3>
            <p>{step.text}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function Landing({ scene }: { scene?: 'look' } = {}): React.ReactElement {
  const phone = usePhone();
  const known = useSession((s) => s.known);
  const authenticated = useSession((s) => s.me.authenticated);
  const access = useSession((s) => s.me.access);
  const reason = useSession((s) => s.me.reason);
  const possible = useSession((s) => s.me.possible);
  const name = useSession((s) => s.me.name);
  const botName = useSession((s) => s.me.botName);
  const paidUntil = until(useSession((s) => s.me.until));

  useEffect(() => {
    document.body.classList.add('nx-body');
    void useSession.getState().load();
    return () => document.body.classList.remove('nx-body');
  }, []);

  // Вошёл и приглашён. Такому человеку страница обязана быть коротким
  // приветствием, а не пересказом продукта, который он уже купил: он пришёл
  // работать, и всё, что стоит между ним и лентой, — это лишний шаг.
  const inside = authenticated && access;

  if (phone) return <PhoneLanding />;

  return (
    <div className="nx-root nx-land">
      {/* Свет за первым экраном. Единственное украшение страницы и единственное
          место, где на тёмном полотне что-то светлее фона: он держит взгляд там,
          где стоит заголовок и кнопка. Ниже сгиба его нет — там работает
          типографика. */}
      <div className="nx-land__glow" aria-hidden="true" />
      <header className="nx-land__top">
        <span className="nx-land__mark">FORQ</span>
        {/* Вошедшему витрина обязана сказать, что он вошёл: иначе он жмёт «войти»
            второй раз, а второе нажатие с другого устройства заводит второй экран
            и упирается в подтверждение — то есть наша немота сама создаёт то, что
            мы потом считаем подозрительным. */}
        <Who />
        {inside && (
          <a className="nx-land__enter" href={appHref()}>
            лента
          </a>
        )}
      </header>

      <section className="nx-land__hero">
        {/* Строка над заголовком отвечает на вопрос «куда я попал» за полсекунды,
            до чтения. Для приглашённого она же говорит, до какого числа открыт
            доступ: это единственное число, которое ему тут интересно. */}
        <p className="nx-land__kick">
          {inside
            ? paidUntil === null
              ? 'вы вошли'
              : `вы вошли · доступ до ${paidUntil}`
            : scene === 'look'
                ? 'плюс уже после лимита'
                : 'сканер вилок · доступ по приглашению'}
        </p>
        {inside ? (
          <h1>
            С возвращением{name === null ? '' : `, ${name}`}
            <br />
            <span className="nx-land__pale">лента идёт прямо сейчас</span>
          </h1>
        ) : (
          <h1>
            Сколько вы заработаете на вилке
            <br />
            <span className="nx-land__pale">и успеете ли её поставить</span>
          </h1>
        )}
        <p className="nx-land__lead">
          {inside
            ? 'Витрина покажет ту вилку, которую стоит брать сейчас, лента - все остальные. ' +
              'Суммы по ногам уже посчитаны вашим банком и вашими лимитами.'
            : 'Вилка - это две ставки на противоположные исходы в разных конторах: как бы ни ' +
              'закончился матч, вы в плюсе. Каждую из двух ставок мы называем ногой. Сканер ' +
              'находит такие пары и сразу считает, сколько с них выйдет именно у вас, с ' +
              'учётом вашего банка, лимитов ваших контор и вашей скорости. Мы делали его под ' +
              'банк 10–100 тысяч.'}
        </p>
        {/* Место под действие занято всегда, даже пока сервер не ответил: без
            этого страница на секунду показывает текст без кнопки и прыгает,
            когда кнопка появляется. Прыжок на первом экране читается как
            «сайт грузится плохо», а грузится он ровно один запрос. */}
        <div className="nx-land__act">
          {known &&
            (inside ? (
              <a className="nx-login__cta nx-login__cta--go" href={appHref()}>
                Открыть ленту
              </a>
            ) : authenticated ? (
              // Вошёл, но приглашения нет. Кнопки здесь нет намеренно: нажимать
              // человеку нечего, и кнопка, которая ничего не меняет, — худший
              // вид обещания. Нужное действие происходит не у нас, а в Telegram.
              <p className="nx-land__closed nx-land__lack">{lack(reason)}</p>
            ) : possible ? (
              <>
                <Login onDone={() => location.assign(appHref())} />
                {/* Почему за дверью пусто. Человек пришёл смотреть вилки и
                    вправе спросить, где они; ответ «лента за входом» он должен
                    прочитать здесь, а не решить, что сканер сломан. */}
                <p className="nx-land__closed">
                  Лента открывается после входа. Мы не показываем её даже частично: имена
                  контор и готовые купоны и есть весь продукт.
                </p>
              </>
            ) : (
              // Контур без входа — не тупик. На зеркальном стенде лента открыта, и
              // фраза «вход закрыт» вместо кнопки отправляла бы человека назад с
              // работающей страницы.
              <a className="nx-login__cta" href={appHref()}>
                Смотреть ленту
              </a>
            ))}
        </div>
        <Alive />
      </section>

      {/* Три шага показываются только тому, кто ещё не внутри, и только там, где
          вход вообще есть. Приглашённому они рассказывают его собственное
          прошлое, а на зеркале — чужой порядок: там пароль, а не бот. */}
      {known && !inside && possible && !MIRROR_CONTOUR && <Steps bot={botName} />}

      <section className="nx-land__grid">
        {promises(MIRROR_CONTOUR).map((point, index) => (
          <article key={point.head} className="nx-land__cell">
            <b className="nx-land__num">{String(index + 1).padStart(2, '0')}</b>
            <h2>{point.head}</h2>
            <p>{point.text}</p>
          </article>
        ))}
      </section>

      <footer className="nx-land__foot">
        <p className="nx-land__age">
          <b>18+</b> Сканер только считает. Ставки делаете вы, у своих букмекеров, на свои
          деньги и под свою ответственность. Прибыль никто не гарантирует: контора вправе
          срезать лимит, аннулировать ставку или закрыть счёт.
        </p>
        <p className="nx-land__age">
          {MIRROR_CONTOUR ? 'Один пароль' : 'Один аккаунт'} рассчитан на одного человека. Если
          передать доступ, он перестанет работать: лента и личные настройки заточены под
          одного.
        </p>
        {/* Что храним — не общая фраза, а список. На контурах он разный, и
            подставить сюда Telegram там, где входа через Telegram нет, значит
            приписать себе данные, которых у нас не существует. */}
        <p className="nx-land__age">
          {MIRROR_CONTOUR
            ? 'Мы храним имя, которым вы вошли, и настройки сканера: банк, лимиты, ' +
              'замеренную скорость. Этого хватает, чтобы ваши числа переехали за вами на ' +
              'другую машину, и мало для чего-то ещё.'
            : 'Мы храним ваш Telegram-идентификатор, список ваших устройств и настройки ' +
              'сканера. Этого хватает, чтобы отличить ваш второй компьютер от чужого ' +
              'человека, и мало для чего-то ещё.'}
        </p>
      </footer>
    </div>
  );
}
